/*
 * Human-made badge brand colours.
 *
 * The badge now adopts the tenant brand colour (theme primary) instead of a
 * fixed indigo, but a trust mark must stay legible on EVERY tenant — including
 * pale or highly saturated brands. So the brand colour is passed through the
 * same accessibility-safe contrast mechanism the primary Button uses
 * (`getSufficientContrast`): the seal/disc background is darkened down the brand
 * ramp until white foreground clears 4.5:1, and the chip's text is darkened
 * until it clears 4.5:1 against the light brand tint. White foregrounds stay
 * white — only the brand surface behind them is adjusted.
 */
import { getSufficientContrast } from '../../theme/palette/helpers'

// The result depends only on the palette, but resolving it walks the shade ramp
// twice. The badge renders on every page and reads these from both the style hook
// and the component body, so memoise per palette rather than per render.
const cache = new WeakMap()

export const getBadgeColors = palette => {
  const cached = cache.get(palette)
  if (cached) return cached

  const white = palette.white.main

  // Seal + chip disc: white icon/label on the brand colour.
  const { contrast: brandSurface } = getSufficientContrast(palette, white, palette.primary.main)

  // Chip pill: dark brand text on a light brand tint.
  const chipBg = palette.primary.shade[5]
  const { contrast: chipFg } = getSufficientContrast(palette, chipBg, palette.primary.shade[60])

  const colors = {
    sealBg: brandSurface,
    sealFg: white,
    chipBg,
    chipFg,
    discBg: brandSurface,
    discFg: white
  }

  cache.set(palette, colors)
  return colors
}
